import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { authRepository } from '@/features/auth/api/auth-repository'
import { matchesRepository } from './api/matches-repository'
import { matchDocToView, type MatchView } from './use-matches'

/**
 * The shared-link door: a WhatsApp recipient WITHOUT the app lands on
 * /match/{id}. Firestore reads require auth, so the page silently opens a
 * GUEST (anonymous) session first — still authenticated traffic, still
 * rate-limitable, and the uid becomes theirs forever if they later register
 * (email signup LINKS the anonymous account instead of replacing it).
 */
export interface UseEnsureSessionResult {
  uid: string | null
  isAnonymous: boolean
  status: 'loading' | 'ready' | 'error'
  retry: () => void
}

export function useEnsureSession(): UseEnsureSessionResult {
  const [state, setState] = useState<{
    uid: string | null
    isAnonymous: boolean
    status: 'loading' | 'ready' | 'error'
  }>({
    uid: null,
    isAnonymous: false,
    status: 'loading',
  })

  const attemptRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef(false)

  const runAttempt = useCallback(() => {
    const attemptId = ++attemptRef.current
    inFlightRef.current = true
    setState((prev) => ({ ...prev, status: 'loading' }))

    let settled = false

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const onTimeout = () => {
      if (settled) return
      if (attemptRef.current !== attemptId) return
      settled = true
      inFlightRef.current = false
      setState((prev) => ({ ...prev, status: 'error' }))
    }

    timerRef.current = setTimeout(onTimeout, 12000)

    let promise: Promise<{ uid: string; isAnonymous: boolean }>
    try {
      promise = authRepository.ensureSignedIn()
    } catch {
      if (settled) return
      if (attemptRef.current !== attemptId) return
      settled = true
      clearTimer()
      inFlightRef.current = false
      setState((prev) => ({ ...prev, status: 'error' }))
      return
    }

    promise
      .then((user) => {
        if (settled) return
        if (attemptRef.current !== attemptId) return
        settled = true
        clearTimer()
        inFlightRef.current = false
        setState({
          uid: user.uid,
          isAnonymous: user.isAnonymous,
          status: 'ready',
        })
      })
      .catch(() => {
        if (settled) return
        if (attemptRef.current !== attemptId) return
        settled = true
        clearTimer()
        inFlightRef.current = false
        setState((prev) => ({ ...prev, status: 'error' }))
      })
  }, [])

  const cancelAttempt = useCallback(() => {
    attemptRef.current += 1
    inFlightRef.current = false
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (AUTH_BYPASS) {
      setState({ uid: null, isAnonymous: false, status: 'ready' })
      return
    }
    runAttempt()
    return cancelAttempt
  }, [runAttempt, cancelAttempt])

  const retry = useCallback(() => {
    if (state.status !== 'error') return
    if (inFlightRef.current) return
    runAttempt()
  }, [state.status, runAttempt])

  return {
    uid: state.uid,
    isAnonymous: state.isAnonymous,
    status: state.status,
    retry,
  }
}

export function usePreviewMatch(id: string | undefined, uid: string | null) {
  return useQuery({
    queryKey: ['match-preview', id],
    queryFn: async () => {
      const doc = await // `id!` — gated by `enabled: !!id`, so the fn never runs without one.
      matchesRepository.getById(id!)
      return doc ? matchDocToView(doc) : null
    },
    enabled: !AUTH_BYPASS && !!id && uid !== null,
    staleTime: 15_000,
  })
}

export function useGuestJoin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { matchId: string; name: string }) => {
      const user = await authRepository.ensureSignedIn()
      await matchesRepository.joinMatch(args.matchId, {
        uid: user.uid,
        name: args.name,
        photoURL: null,
        gender: null,
        isGuest: user.isAnonymous,
      })
      return user.uid
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['match-preview'] })
      void qc.invalidateQueries({ queryKey: ['match'] })
      void qc.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

export type PreviewMatch = MatchView
